import { Form, Formik } from 'formik';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';
import { Partner } from 'src/reducers/partner/types';

import { getPartnerTranslation, getPartnerTranslationKey } from 'Utilities/helpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import * as yup from 'Utilities/yup';

import {
  ERROR_PARTNERS_ACCEPT_CONSENT,
  ERROR_PARTNERS_DECLINE_CONSENT,
} from 'Constants/errorTypes';
import { PARTNER_TYPES } from 'Constants/partnerTypes';
import { SCOPE_TYPE, SCOPE_TYPE_READ, SCOPE_TYPE_WRITE } from 'Constants/scopeTypes';

import ScopeSection from 'Components/consent/ScopeSection';
import HtmlTranslation from 'Components/HtmlTranslation';

const mapState = ({
  config: { privacyPolicy, termsOfUse },
  user: { code, yearOfBirth, phoneLanguage },
}: RootState) => {
  return {
    code,
    yearOfBirth,
    abbottPrivacy:
      (privacyPolicy && (privacyPolicy[phoneLanguage] || privacyPolicy['default'])) || '',
    abbottTOS: (termsOfUse && (termsOfUse[phoneLanguage] || termsOfUse['default'])) || '',
  };
};

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector> & {
  partner: Partner;
  needsMigration: boolean;
  code: string;
  yearOfBirth: number;
};

type ConsentFormValues = {
  readToggle: boolean;
  writeToggle: boolean;
};

type ScopeMap = Record<SCOPE_TYPE, string[]>;

const INITIAL_VALUES: ConsentFormValues = {
  readToggle: false,
  writeToggle: false,
};

const validationSchema = yup
  .object({
    readToggle: yup.boolean(),
    writeToggle: yup.boolean(),
  })
  .test('toggleCheck', i18n.t('Consent.toggleConsent'), ({ readToggle, writeToggle }) =>
    Boolean(readToggle || writeToggle)
  );

const declineConsent = (
  code: string,
  partnerId: string,
  yearOfBirth: number,
  needsMigration: boolean
) => {
  const topic: string = needsMigration ? 'partner:declineReconsent' : 'partner:declineConsent';

  mediator
    .request(topic, {
      code,
      yearOfBirth,
      partnerId,
    })
    .catch((err) => {
      mediator.publish('error:display', {
        error: {
          code: err.status,
          type: ERROR_PARTNERS_DECLINE_CONSENT,
        },
      });
    });
};

const acceptConsent = (
  code: string,
  partnerId: string,
  yearOfBirth: number,
  needsMigration: boolean
) => {
  const topic: string = needsMigration ? 'partner:acceptReconsent' : 'partner:acceptConsent';

  mediator
    .request(topic, {
      code,
      yearOfBirth,
      partnerId,
    })
    .catch((err) => {
      mediator.publish('router:navigate', '/');

      mediator.publish('error:display', {
        error: {
          code: err.status,
          type: ERROR_PARTNERS_ACCEPT_CONSENT,
        },
      });
    });
};

const createScopeMap = (scopes: string[]): ScopeMap => {
  return scopes.reduce<ScopeMap>(
    (scopeMap, scope) => {
      const [scopeType] = scope.split('.');

      if (scopeType === SCOPE_TYPE_READ || scopeType === SCOPE_TYPE_WRITE) {
        scopeMap[scopeType].push(scope);
      }

      return scopeMap;
    },
    {
      [SCOPE_TYPE_READ]: [],
      [SCOPE_TYPE_WRITE]: [],
    }
  );
};

const getHeaderKey = (scopeMap: ScopeMap): string => {
  const hasReadHealthData = scopeMap[SCOPE_TYPE_READ].includes('read.healthdata');
  const hasWriteHealthData = scopeMap[SCOPE_TYPE_WRITE].includes('write.healthdata');

  if (hasReadHealthData && hasWriteHealthData) {
    return 'requestingSharingAndReceivingAccess';
  }

  if (hasReadHealthData) {
    return 'requestingSharingAccess';
  }

  if (hasWriteHealthData) {
    return 'requestingReceivingAccess';
  }

  return 'confirmSharing';
};

const ManageConsent = ({
  abbottPrivacy,
  abbottTOS,
  code,
  partner,
  yearOfBirth,
  needsMigration,
}: Props) => {
  if (partner.type !== PARTNER_TYPES.COMMERCIAL_API) {
    return null;
  }

  const scopeMap = createScopeMap(partner.scopes);

  if (scopeMap[SCOPE_TYPE_READ].length === 0 && scopeMap[SCOPE_TYPE_WRITE].length === 0) {
    return null;
  }

  const headerKey = getHeaderKey(scopeMap);

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={validationSchema}
      onSubmit={() => acceptConsent(code, partner.id, yearOfBirth, needsMigration)}
    >
      {({ isSubmitting, isValid, dirty }) => (
        <Form>
          <div>
            <div className="mb-3">
              <header className="d-flex justify-content-between align-items-center">
                <div className="d-flex">
                  <span className="text-xl text-black font-weight-bold">
                    {getPartnerTranslation('Consent', partner.type, headerKey, {
                      partnerName: partner.name,
                    })}
                  </span>
                </div>
              </header>
            </div>

            <ScopeSection
              scopeType={SCOPE_TYPE_READ}
              scopes={scopeMap[SCOPE_TYPE_READ]}
              partnerName={partner.name}
              partnerType={partner.type}
              scopeDetails={partner.scopeDetails}
            />

            {scopeMap[SCOPE_TYPE_READ].length > 0 && <hr />}

            <ScopeSection
              scopeType={SCOPE_TYPE_WRITE}
              scopes={scopeMap[SCOPE_TYPE_WRITE]}
              partnerName={partner.name}
              partnerType={partner.type}
              scopeDetails={partner.scopeDetails}
            />

            {scopeMap[SCOPE_TYPE_WRITE].length > 0 && <hr />}

            {needsMigration && (
              <HtmlTranslation
                id="consentForm-privacy-text"
                className="mb-3 text-black-50 d-block"
                translationKey={getPartnerTranslationKey(
                  'PartnersGeneric',
                  partner.type,
                  'healthDataDeclinePriorConsentRevoked'
                )}
                translationProps={{
                  partnerName: partner.name,
                }}
              />
            )}

            {!partner.hideDisclaimer && (
              <HtmlTranslation
                id="consentForm-privacy-text"
                className="mb-3 text-black-50 d-block"
                translationKey={getPartnerTranslationKey(
                  'Consent',
                  partner.type,
                  'accessDisclaimer'
                )}
                translationProps={{
                  partnerName: partner.name,
                }}
              />
            )}

            <HtmlTranslation
              id={
                partner.hideDisclaimer
                  ? 'consentForm-privacy-text'
                  : 'consentForm-privacy-statement'
              }
              className="mb-3 text-black-50 d-block"
              translationKey={getPartnerTranslationKey('Consent', partner.type, 'privacyStatement')}
              translationProps={{
                partnerName: partner.name,
                abbottPrivacy,
                abbottTOS,
              }}
            />

            <div className="row justify-content-between">
              <div className="col-6 col-sm-4 pr-2">
                <button
                  type="button"
                  className="btn btn-secondary text-lg font-weight-bold w-100"
                  onClick={() => declineConsent(code, partner.id, yearOfBirth, needsMigration)}
                >
                  {getPartnerTranslation('Common', partner.type, 'decline_cap')}
                </button>
              </div>
              <div className="col-6 col-sm-4 pl-2">
                <button
                  disabled={isSubmitting || !isValid || !dirty}
                  type="button"
                  className="btn btn-primary text-lg font-weight-bold w-100"
                  onClick={() => acceptConsent(code, partner.id, yearOfBirth, needsMigration)}
                >
                  {getPartnerTranslation('Common', partner.type, 'allow_cap')}
                </button>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default connector(ManageConsent);
